import { useState, useEffect, useRef } from 'react';

// SHA-256 of "AmuVcTeddyBB13*", used as fallback when env var is absent.
// Reversing SHA-256 is computationally infeasible, so storing the digest is safe.
const FALLBACK_HASH = 'dc0103c6552eb18e33738f100134c4d6b6e601c39dc0fe167b941b30a0c685a8';

const SESSION_KEY = '__pg_auth__';

async function sha256hex(str: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(str),
  );
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// Resolve the correct hash once at module load:
//, if VITE_APP_PASSWORD is baked in at build time, hash that value
//, otherwise fall back to the hardcoded digest above
const ENV_PW = import.meta.env.VITE_APP_PASSWORD as string | undefined;
const correctHashPromise: Promise<string> = ENV_PW
  ? sha256hex(ENV_PW)
  : Promise.resolve(FALLBACK_HASH);

function isUnlocked(): boolean {
  try { return sessionStorage.getItem(SESSION_KEY) === '1'; }
  catch { return false; }
}

function unlock() {
  try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* */ }
}

interface PasswordGateProps {
  children: React.ReactNode;
}

export function PasswordGate({ children }: PasswordGateProps) {
  const [passed,   setPassed]   = useState(isUnlocked);
  const [value,    setValue]    = useState('');
  const [error,    setError]    = useState(false);
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!passed) inputRef.current?.focus();
  }, [passed]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!value || checking) return;
    setChecking(true);
    setError(false);
    const [inputHash, correctHash] = await Promise.all([
      sha256hex(value),
      correctHashPromise,
    ]);
    if (inputHash === correctHash) {
      unlock();
      setPassed(true);
    } else {
      setError(true);
      setValue('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    setChecking(false);
  }

  if (passed) return <>{children}</>;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'var(--bg-base, #0F1123)',
      zIndex: 99999,
      padding: '1.5rem',
    }}>
      <div style={{
        width: '100%', maxWidth: '22rem',
        backgroundColor: 'var(--bg-surface, #161829)',
        border: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
        borderRadius: '1rem',
        padding: '2.5rem 2rem',
        boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
        textAlign: 'center',
      }}>
        <p style={{
          fontFamily: 'var(--font-display, serif)',
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--petrol)',
          margin: '0 0 0.25rem',
          letterSpacing: '0.02em',
        }}>
          ✦ Professya
        </p>
        <p style={{
          fontSize: '0.8rem',
          color: 'var(--text-muted, #64748b)',
          margin: '0 0 2rem',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}>
          Site inativo
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <input
            ref={inputRef}
            type="password"
            value={value}
            onChange={e => { setValue(e.target.value); setError(false); }}
            placeholder="Senha de acesso"
            autoComplete="current-password"
            disabled={checking}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              border: `1px solid ${error ? 'rgba(239,68,68,0.6)' : 'var(--border-subtle, rgba(255,255,255,0.1))'}`,
              background: 'var(--bg-base, #0F1123)',
              color: 'var(--text-primary, #f1f5f9)',
              fontSize: '1rem',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 150ms',
            }}
          />

          {error && (
            <p style={{ margin: 0, color: '#f87171', fontSize: '0.85rem' }}>
              Incorrect password. Please try again.
            </p>
          )}

          <button
            type="submit"
            disabled={!value || checking}
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              border: 'none',
              background: 'var(--petrol)',
              color: '#0F1123',
              fontWeight: 700,
              fontSize: '0.9rem',
              cursor: !value || checking ? 'not-allowed' : 'pointer',
              opacity: !value || checking ? 0.6 : 1,
              transition: 'opacity 150ms',
            }}
          >
            {checking ? 'Verificando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}
